<template>
  <div class="bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700">
    <div class="p-4">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-6">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-6">
        <p class="text-error-500 text-sm">{{ error }}</p>
      </div>

      <!-- Order Details -->
      <div v-else-if="order" class="space-y-4">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <!-- Left Column: Order Details -->
          <div class="lg:col-span-2 space-y-3">
            <!-- Order Information -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <div class="flex justify-between items-start mb-3">
                <div>
                  <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">Order Information</h3>
                  <p class="text-xs text-surface-500 dark:text-surface-400">Created {{ formatDate(order.created_at) }}
                  </p>
                </div>
                <span class="px-2 py-1 text-xs rounded-full" :class="getStatusClass(order.status)">
                  {{ formatStatus(order.status) }}
                  <!-- Production stage indicator -->
                  <span v-if="order.status === 'production' && order.sub_status" class="ml-1">
                    <i v-if="order.sub_status === 'layouting'" class="fas fa-layer-group" title="Layouting"></i>
                    <i v-else-if="order.sub_status === 'printing'" class="fas fa-print" title="Printing"></i>
                    <i v-else-if="order.sub_status === 'operations'" class="fas fa-tools" title="Operations"></i>
                  </span>
                </span>
              </div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <p class="font-medium text-surface-500 dark:text-surface-400">Order Number</p>
                  <p class="text-surface-900 dark:text-surface-100">#{{ order.order_number }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Department</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.department?.name || 'Not assigned' }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Gateway</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.gateway_name }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Order Type</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.order_type?.name || 'Not specified' }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Deadline</p>
                  <p :class="getDueDateClass(order.deadline)">
                    {{ formatDate(order.deadline) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Client Information -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">Client Information</h3>
              <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <p class="font-medium text-surface-500 dark:text-surface-400">Client Name</p>
                  <div class="flex items-center gap-2">
                    <div>
                      <p class="text-surface-900 dark:text-surface-100">{{ order.client_name }}</p>
                      <p v-if="order.company_name" class="text-surface-600 dark:text-surface-400 mt-0.5">{{
                        order.company_name }}</p>
                    </div>
                    <span v-if="getClientClassification(order)" class="px-2 py-0.5 text-xs rounded-full"
                      :class="getClassificationClass(getClientClassification(order))">
                      {{ formatClassification(getClientClassification(order)) }}
                    </span>
                  </div>
                </div>
                <div v-if="order.client_contact">
                  <p class="font-medium text-surface-500 dark:text-surface-400">Contact Number</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.client_contact }}</p>
                </div>
              </div>
            </div>

            <!-- Payment Information -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <div class="flex justify-between items-center mb-3">
                <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">Payment Information</h3>
                <span class="px-2 py-1 text-xs rounded-full font-medium" :class="getPaymentStatusClass(paymentStatus)">
                  {{ formatPaymentStatus(paymentStatus) }}
                </span>
              </div>

              <!-- Payment Summary -->
              <div class="grid grid-cols-3 gap-2 mb-3 p-2 bg-surface-50 dark:bg-surface-700/50 rounded text-xs">
                <div class="text-center">
                  <p class="font-medium text-surface-500 dark:text-surface-400">Total Amount</p>
                  <p class="font-semibold text-surface-900 dark:text-surface-100">₱{{ formatPrice(order.total_amount) }}
                  </p>
                </div>
                <div class="text-center">
                  <p class="font-medium text-surface-500 dark:text-surface-400">Total Paid</p>
                  <p class="font-semibold text-success-600 dark:text-success-400">₱{{ formatPrice(totalPaid) }}</p>
                </div>
                <div class="text-center">
                  <p class="font-medium text-surface-500 dark:text-surface-400">Outstanding Balance</p>
                  <p class="font-semibold"
                    :class="outstandingBalance > 0 ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'">
                    ₱{{ formatPrice(outstandingBalance) }}
                  </p>
                </div>
              </div>

              <!-- Individual Payments -->
              <div v-if="order.payments && order.payments.length > 0" class="space-y-2">
                <h4 class="text-xs font-medium text-surface-700 dark:text-surface-300">Payment History</h4>
                <div class="space-y-1 max-h-20 overflow-y-auto">
                  <div v-for="payment in order.payments" :key="payment.id"
                    class="flex justify-between items-center p-2 bg-surface-50 dark:bg-surface-700/50 rounded text-xs">
                    <div>
                      <p class="font-medium text-surface-900 dark:text-surface-100">₱{{ formatPrice(payment.amount) }}
                      </p>
                      <p class="text-surface-500 dark:text-surface-400">{{ formatDate(payment.payment_date) }}</p>
                    </div>
                    <div class="text-right">
                      <span class="px-1.5 py-0.5 text-xs rounded-full"
                        :class="getPaymentItemStatusClass(payment.status)">
                        {{ formatStatus(payment.status) }}
                      </span>
                      <p v-if="payment.payment_method" class="text-surface-500 dark:text-surface-400 mt-0.5">{{
                        payment.payment_method }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Payments -->
              <div v-else class="text-center py-2">
                <p class="text-xs text-surface-500 dark:text-surface-400">No payments recorded</p>
              </div>

              <!-- Action Buttons -->
              <div class="mt-3 pt-3 border-t border-surface-200 dark:border-surface-600">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <!-- Add Payment Button -->
                  <button v-if="hasPermission('payments.create')" @click="openPaymentModal"
                    :disabled="!hasPermission('payments.create') || isPaymentDisabled" :title="getPaymentButtonTooltip"
                    class="px-3 py-2 bg-success-600 text-white text-xs rounded-md hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    <i class="fas fa-money-bill-wave"></i>
                    <span>Add Payment</span>
                  </button>

                  <!-- Released Button -->
                  <button v-if="hasPermission('orders.complete')" @click="openReleaseModal"
                    :disabled="!canMarkAsReleased || isProcessingRelease" :title="getReleaseButtonTooltip"
                    class="px-3 py-2 text-white text-xs rounded-md flex items-center justify-center gap-2 transition-colors"
                    :class="canMarkAsReleased && !isProcessingRelease
                      ? 'bg-secondary-600 hover:bg-secondary-700'
                      : 'bg-surface-400 cursor-not-allowed'">
                    <i v-if="isProcessingRelease" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-shipping-fast"></i>
                    <span>{{ isProcessingRelease ? 'Releasing...' : 'Released' }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- User Information -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">User Information</h3>
              <div class="grid grid-cols-3 gap-x-4 gap-y-2 text-xs">
                <div>
                  <p class="font-medium text-surface-500 dark:text-surface-400">Assigned To</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.assigned_to_user_name || 'Not assigned' }}
                  </p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Assigned Operations</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.assigned_operations_user_name ||
                    'Not assigned' }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-500 dark:text-dark-400">Created By</p>
                  <p class="text-surface-900 dark:text-surface-100">{{ order.created_by_user_name || 'System' }}</p>
                </div>
              </div>
            </div>


          </div>

          <!-- Right Column: Order Items and Image -->
          <div class="lg:col-span-1 space-y-3">
            <!-- Order Items -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">Order Items</h3>
              <div class="space-y-2 max-h-32 overflow-y-auto">
                <template v-if="order.old_products">
                  <div class="text-xs text-surface-600 dark:text-surface-400">{{ order.old_products }}</div>
                </template>
                <template v-else>
                  <div v-for="item in order.items" :key="item.id"
                    class="py-1 border-b border-surface-100 dark:border-surface-600 last:border-0">
                    <div class="text-xs">
                      <p class="text-surface-900 dark:text-surface-100 font-medium">{{ item.product_name }}</p>
                      <div class="text-surface-600 dark:text-surface-400 mt-0.5 space-y-0.5">
                        <p>Qty: {{ item.quantity }}</p>
                        <div v-if="item.has_dimensions || item.width || item.height">
                          <p>Size: {{ item.width || 0 }}x{{ item.height || 0 }} {{ item.dimension_unit || 'units' }}</p>
                        </div>
                        <div v-if="item.specifications">
                          <p>Specs: {{ item.specifications }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Order Image -->
            <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">Order Image</h3>
              <div v-if="order.image_url" class="relative">
                <img :src="getImageUrl(order.image_url)" alt="Order Image"
                  class="w-full h-32 object-cover rounded border border-surface-200 dark:border-surface-600 shadow-sm"
                  @error="handleImageError" @load="handleImageLoad" />
                <button @click="openImagePreview(order.image_url)"
                  class="absolute bottom-1 right-1 bg-surface-0 bg-opacity-90 p-1 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                  title="View full image">
                  <i class="fas fa-expand text-surface-600 text-xs"></i>
                </button>
              </div>
              <div v-else
                class="flex items-center justify-center h-32 bg-surface-50 dark:bg-surface-700/50 rounded border-2 border-dashed border-surface-300 dark:border-surface-600">
                <div class="text-center">
                  <i class="fas fa-image text-gray-400 dark:text-gray-500 text-xl mb-1"></i>
                  <p class="text-xs text-surface-500 dark:text-surface-400">No image available</p>
                </div>
              </div>
            </div>
            <!-- Notes -->
            <div v-if="order.notes"
              class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-3">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">Notes</h3>
              <p class="text-xs text-surface-600 dark:text-surface-400 whitespace-pre-wrap max-h-40 overflow-y-auto">{{
                order.notes
              }}</p>
            </div>
          </div>
        </div>

        <!-- Approval Actions (only shown when opened from approval context) -->
        <div v-if="isApprovalContext && order && hasPermission('orders.approve')"
          class="mt-6 pt-6 border-t border-surface-200 dark:border-surface-600">
          <div class="flex flex-col sm:flex-row gap-3">
            <button @click="handleApprove" :disabled="isProcessingApproval"
              class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-success-600 text-white text-sm font-medium rounded-lg hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-success-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ isProcessingApproval ? 'Approving...' : 'Approve Order' }}
            </button>
            <button @click="handleReject" :disabled="isProcessingApproval"
              class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-error-600 text-white text-sm font-medium rounded-lg hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-error-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-times"></i>
              {{ isProcessingApproval ? 'Processing...' : 'Reject Order' }}
            </button>
          </div>
        </div>
      </div>

      <!-- No Data State -->
      <div v-else class="text-center py-6">
        <p class="text-surface-500 dark:text-surface-400 text-sm">No order details available</p>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <Modal v-if="showImagePreview" v-model="showImagePreview" size="xl">
      <template #title>
        Order Image Preview
      </template>
      <template #content>
        <div class="relative">
          <img :src="selectedImage" alt="Order Image" class="w-full h-auto rounded-lg" />
        </div>
      </template>
      <template #footer>
        <div class="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <button type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-surface-900 shadow-sm ring-1 ring-inset ring-surface-300 hover:bg-surface-50 sm:mt-0 sm:w-auto"
            @click="showImagePreview = false">
            Close
          </button>
        </div>
      </template>
    </Modal>

    <!-- Payment Modal -->
    <Modal v-if="showPaymentModal" v-model="showPaymentModal" size="5xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-money-bill-wave text-success-600"></i>
          <span>Add Payment</span>
          <span v-if="order" class="text-sm text-surface-500">- {{ order.order_number }}</span>
        </div>
      </template>
      <template #content>
        <div class="p-2 sm:p-4">
          <PaymentForm :initial-order="order" @cancel="showPaymentModal = false" @submit="handlePaymentSubmit" />
        </div>
      </template>
    </Modal>

    <!-- Release Modal -->
    <Modal v-if="showReleaseModal" v-model="showReleaseModal" size="lg">
      <template #title>
        <div class="flex items-center gap-2">
          <i
            :class="isStuckOrder ? 'fas fa-exclamation-triangle text-warning-600' : 'fas fa-shipping-fast text-secondary-600'"></i>
          <span>{{ isStuckOrder ? 'Mark Order for Automatic Release' : 'Mark Order as Released' }}</span>
          <span v-if="order" class="text-sm text-surface-500">- {{ order.order_number }}</span>
        </div>
      </template>
      <template #content>
        <div v-if="order" class="p-4 space-y-4">
          <!-- Warning for Stuck Orders -->
          <div v-if="isStuckOrder"
            class="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <i class="fas fa-exclamation-triangle text-warning-600 dark:text-warning-400 mt-0.5"></i>
              <div class="flex-1">
                <h4 class="font-semibold text-warning-900 dark:text-warning-200 mb-2">Order is Stuck</h4>
                <p class="text-sm text-warning-800 dark:text-warning-300 mb-2">
                  Order #{{ order.order_number }} is currently in <strong>{{ formatStatus(order.status) }}</strong> but
                  needs to be released.
                </p>
                <div class="text-sm text-warning-700 dark:text-warning-300 space-y-1">
                  <p><strong>This will:</strong></p>
                  <ul class="list-disc list-inside ml-2 space-y-1">
                    <li>Mark the order for automatic release</li>
                    <li>Order will stay in current status ({{ formatStatus(order.status) }})</li>
                    <li>Operations/QC staff will be notified</li>
                    <li>When order reaches ready_release, it will automatically be released</li>
                  </ul>
                </div>
                <p class="text-sm font-medium text-warning-900 dark:text-warning-200 mt-3">
                  ⚠️ Please notify operations or QC staff to complete the order workflow.
                </p>
              </div>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="bg-surface-50 dark:bg-surface-700/50 p-3 rounded-lg">
            <h4 class="font-medium text-surface-900 dark:text-surface-100 mb-2">Order Summary</h4>
            <div class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span class="text-surface-600 dark:text-surface-400">Client:</span>
                <div class="font-medium text-surface-900 dark:text-surface-100">{{ order.client_name }}</div>
                <div v-if="order.company_name" class="text-xs text-surface-600 dark:text-surface-400">{{
                  order.company_name }}</div>
              </div>
              <div>
                <span class="text-surface-600 dark:text-surface-400">Current Status:</span>
                <div class="font-medium text-surface-900 dark:text-surface-100">{{ formatStatus(order.status) }}</div>
              </div>
              <div>
                <span class="text-surface-600 dark:text-surface-400">Total Amount:</span>
                <div class="font-medium text-surface-900 dark:text-surface-100">₱{{ formatPrice(order.total_amount) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Release Type Selection (only for ready_release orders) -->
          <div v-if="!isStuckOrder">
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Release Type <span class="text-error-500">*</span>
            </label>
            <select v-model="releaseForm.releaseType"
              class="w-full px-3 py-2 border border-surface-300 dark:border-surface-600 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary-500 dark:focus:ring-secondary-400 focus:border-secondary-500 dark:focus:border-secondary-400 text-sm bg-white dark:bg-surface-700 text-surface-900 dark:text-surface-100"
              required>
              <option value="">Select release type...</option>
              <option value="pickup">Customer Pickup</option>
              <option value="delivery">Delivery</option>
              <option value="courier">Courier Service</option>
            </select>
          </div>

          <!-- Notes -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              {{ isStuckOrder ? 'Notes' : 'Release Notes' }} <span v-if="isStuckOrder">(Optional)</span>
            </label>
            <textarea v-model="releaseForm.notes" rows="3"
              class="w-full px-3 py-2 border border-surface-300 dark:border-surface-600 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary-500 dark:focus:ring-secondary-400 focus:border-secondary-500 dark:focus:border-secondary-400 text-sm bg-white dark:bg-surface-700 text-surface-900 dark:text-surface-100"
              :placeholder="isStuckOrder
                ? 'Add notes about why the order is stuck and needs to be marked for release (e.g., order already completed but status not updated)...'
                : 'Add any additional notes about the release (e.g., who received it, special instructions, etc.)'"></textarea>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <button type="button" @click="confirmRelease"
            :disabled="(!isStuckOrder && !releaseForm.releaseType) || isProcessingRelease"
            class="w-full sm:w-auto inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3"
            :class="isStuckOrder ? 'bg-warning-600 hover:bg-warning-700' : 'bg-secondary-600 hover:bg-secondary-500'">
            <i v-if="isProcessingRelease" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else :class="isStuckOrder ? 'fas fa-flag mr-2' : 'fas fa-shipping-fast mr-2'"></i>
            {{ isProcessingRelease
              ? (isStuckOrder ? 'Marking...' : 'Releasing...')
              : (isStuckOrder ? 'Mark for Auto-Release' : 'Mark as Released') }}
          </button>
          <button type="button" @click="closeReleaseModal" :disabled="isProcessingRelease"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-surface-900 shadow-sm ring-1 ring-inset ring-surface-300 hover:bg-surface-50 disabled:opacity-50 sm:mt-0 sm:w-auto">
            Cancel
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useApi } from '@/composables/useApi';
import { useAuth } from '@/composables/useAuth';
import { useToast } from '@/composables/useToast';
import Modal from '@/components/common/Modal.vue';
import PaymentForm from '@/components/payments/PaymentForm.vue';
import { parseISO, isPast, isToday } from 'date-fns';

const props = defineProps({
  orderId: {
    type: [Number, String],
    required: true
  },
  isApprovalContext: {
    type: Boolean,
    default: false
  }
});

const api = useApi();
const { hasPermission } = useAuth();
const toast = useToast();
const loading = ref(true);
const error = ref(null);
const order = ref(null);

const showImagePreview = ref(false);
const selectedImage = ref(null);
const showPaymentModal = ref(false);
const showReleaseModal = ref(false);

// Approval state
const isProcessingApproval = ref(false);

// Release state
const isProcessingRelease = ref(false);
const releaseForm = ref({
  releaseType: '',
  notes: ''
});

// Payment calculations
const totalPaid = computed(() => {
  if (!order.value?.payments) return 0;
  return order.value.payments
    .filter(payment => payment.status === 'approved')
    .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
});

const outstandingBalance = computed(() => {
  const total = parseFloat(order.value?.total_amount || 0);
  return total - totalPaid.value;
});

const paymentStatus = computed(() => {
  const total = parseFloat(order.value?.total_amount || 0);
  const paid = totalPaid.value;

  if (paid === 0) return 'unpaid';
  if (paid >= total) return 'paid';
  return 'partial';
});

// Payment button logic
const isPaymentDisabled = computed(() => {
  if (!order.value) return true;

  // Disable if order is completed, cancelled, or rejected
  if (['completed', 'cancelled', 'rejected'].includes(order.value.status)) {
    return true;
  }

  // Disable if order is fully paid
  const totalAmount = parseFloat(order.value.total_amount || 0);
  const paid = totalPaid.value;

  if (totalAmount > 0 && paid >= totalAmount) {
    return true;
  }

  return false;
});

const getPaymentButtonTooltip = computed(() => {
  if (!hasPermission('payments.create')) {
    return 'You do not have permission to create payments';
  }

  if (!order.value) return 'Order not available';

  if (order.value.status === 'completed') {
    return 'Order is already completed';
  }

  if (['cancelled', 'rejected'].includes(order.value.status)) {
    return `Order is ${order.value.status}`;
  }

  if (isPaymentDisabled.value) {
    return 'Order is fully paid';
  }

  return 'Add payment for this order';
});

// Release button logic
const canMarkAsReleased = computed(() => {
  if (!order.value || !hasPermission('orders.complete')) return false;

  // Disable if already marked for release
  if (order.value.mark_as_released) return false;

  const status = order.value.status;
  const excludedStatuses = ['pending', 'completed', 'rejected', 'cancelled', 'released'];

  // Allow for ready_release (normal release) or any other status except excluded ones (stuck orders)
  return !excludedStatuses.includes(status);
});

// Check if order is stuck (not ready_release but can be marked for release)
const isStuckOrder = computed(() => {
  if (!order.value) return false;
  return order.value.status !== 'ready_release' && canMarkAsReleased.value;
});

const getReleaseButtonTooltip = computed(() => {
  if (!hasPermission('orders.complete')) {
    return 'You do not have permission to mark orders as released';
  }

  if (!order.value) return 'Order not available';

  if (order.value.status === 'ready_release') {
    return 'Mark order as released (customer picked up or received delivery)';
  }

  if (order.value.status === 'released') {
    return 'Order has already been released';
  }

  if (order.value.status === 'completed') {
    return 'Order has already been completed';
  }

  if (['cancelled', 'rejected'].includes(order.value.status)) {
    return `Order is ${formatStatus(order.value.status)} and cannot be released`;
  }

  // For stuck orders
  return `Order is stuck in ${formatStatus(order.value.status)}. Mark for automatic release when order reaches ready_release.`;
});

const getClientClassification = (order) => {
  if (!order) return '';

  // Only use classification from client relation (clients table)
  if (order.client && order.client.classification) {
    return order.client.classification;
  }

  // Check for client_classification field from the backend response
  if (order.client_classification) {
    return order.client_classification;
  }

  // If client relation is not available, use the classification field that was set in the store
  if (order.classification) {
    return order.classification;
  }

  // Default if no classification is available
  return 'regular';
};

const formatClassification = (classification) => {
  if (!classification) return '';

  // Return the exact classification string as-is from the database
  return classification;
};

const getClassificationClass = (classification) => {
  if (!classification) return '';

  // Map of exact classification strings to their display classes
  const classMap = {
    'vip': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    'regular': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    'new': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'preferred': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
    'inactive': 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300'
  };

  return classMap[classification.toLowerCase()] || 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300';
};

const getDueDateClass = (deadline) => {
  if (!deadline) return 'text-surface-600 dark:text-surface-400';
  const date = parseISO(deadline);
  if (isPast(date) && !isToday(date)) return 'text-error-600 dark:text-error-400 font-semibold';
  if (isToday(date)) return 'text-warning-600 dark:text-warning-400 font-semibold';
  return 'text-success-600 dark:text-success-400';
};

const loadOrderPreview = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/orders/${props.orderId}`, {
      params: {
        include: ['department', 'order_type', 'client', 'assigned_to_user', 'created_by_user', 'payments']
      }
    });
    order.value = response.data;

    // Add classification debug log
    console.log(`[Classification Debug] OrderPreview - Order ${order.value.order_number}:`, {
      'order.client?.classification': order.value.client?.classification,
      'order.classification': order.value.classification,
      'final classification used': getClientClassification(order.value)
    });
  } catch (err) {
    console.error('Error loading order preview:', err);
    error.value = err.response?.data?.message || 'Failed to load order preview';
  } finally {
    loading.value = false;
  }
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatStatus = (status) => {
  if (!status) return '';
  status = String(status);
  const statusMap = {
    'pending': 'Pending',
    'in_review': 'In Review',
    'production': 'Production',
    'quality_check': 'Quality Check',
    'ready_release': 'Ready for Release',
    'released': 'Released',
    'completed': 'Completed',
    'cancelled': 'Cancelled'
  };
  return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getStatusClass = (status) => {
  const statusClasses = {
    'pending': 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300',
    'in_review': 'bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-300',
    'production': 'bg-secondary-100 dark:bg-secondary-900/30 text-secondary-800 dark:text-secondary-300',
    'quality_check': 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300',
    'ready_release': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'released': 'bg-secondary-100 dark:bg-secondary-900/30 text-secondary-800 dark:text-secondary-300',
    'completed': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'cancelled': 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300'
  };
  return statusClasses[status] || 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300';
};

const formatPaymentStatus = (status) => {
  const statusMap = {
    'paid': 'Fully Paid',
    'partial': 'Partially Paid',
    'unpaid': 'Unpaid'
  };
  return statusMap[status] || status;
};

const getPaymentStatusClass = (status) => {
  const statusClasses = {
    'paid': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'partial': 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300',
    'unpaid': 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300'
  };
  return statusClasses[status] || 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300';
};

const getPaymentItemStatusClass = (status) => {
  const statusClasses = {
    'approved': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'pending': 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300',
    'rejected': 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300'
  };
  return statusClasses[status] || 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-300';
};

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  if (imageUrl.startsWith('data:')) return imageUrl;
  const apiBaseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_URL?.replace(/\/api$/, '') || '';
  return `${apiBaseUrl}/${imageUrl}`;
};

const handleImageError = (e) => {
  console.error('Error loading image:', e);
  e.target.src = ''; // Clear the src to show the no-image placeholder
};

const handleImageLoad = () => {
  // Image loaded successfully
};

const openImagePreview = (imageUrl) => {
  selectedImage.value = getImageUrl(imageUrl);
  showImagePreview.value = true;
};

// Payment modal functions
const openPaymentModal = () => {
  showPaymentModal.value = true;
};

const handlePaymentSubmit = async (paymentData) => {
  try {
    console.log('Submitting payment:', paymentData);
    const response = await api.post('/payments', paymentData);

    // Check if this was a duplicate submission
    if (response?.data?.duplicate) {
      toast.warning('This payment was already recorded. No duplicate was created.', { duration: 5000 });
      console.log('[Payment] Duplicate detected, existing payment returned:', response.data.payment);
    } else {
      toast.success('Payment added successfully');
    }

    showPaymentModal.value = false;

    // Refresh order data to show new payment
    await loadOrderPreview();
  } catch (error) {
    console.error('Error adding payment:', error);
    toast.error(`Failed to add payment: ${error.response?.data?.message || error.message}`);
  }
};

// Approval handlers
const handleApprove = async () => {
  if (!order.value || isProcessingApproval.value) return;

  try {
    isProcessingApproval.value = true;

    const response = await api.post(`/orders/${order.value.id}/approve`, {
      status: 'approved',
      approved_by: 'current_user' // This should be handled by the backend
    });

    if (response.data) {
      toast.success('Order approved successfully');

      // Refresh the order data to show updated status
      await loadOrderPreview();

      // Emit event to parent component to refresh the approvals list
      emit('approved', order.value);
    }
  } catch (error) {
    console.error('Error approving order:', error);
    toast.error(error.response?.data?.message || 'Failed to approve order');
  } finally {
    isProcessingApproval.value = false;
  }
};

const handleReject = async () => {
  if (!order.value || isProcessingApproval.value) return;

  const reason = prompt('Please provide a reason for rejecting this order:');
  if (!reason || reason.trim() === '') {
    toast.warning('Please provide a reason for rejection');
    return;
  }

  try {
    isProcessingApproval.value = true;

    const response = await api.post(`/orders/${order.value.id}/reject`, {
      status: 'rejected',
      rejection_reason: reason.trim(),
      rejected_by: 'current_user' // This should be handled by the backend
    });

    if (response.data) {
      toast.success('Order rejected successfully');

      // Refresh the order data to show updated status
      await loadOrderPreview();

      // Emit event to parent component to refresh the approvals list
      emit('rejected', order.value);
    }
  } catch (error) {
    console.error('Error rejecting order:', error);
    toast.error(error.response?.data?.message || 'Failed to reject order');
  } finally {
    isProcessingApproval.value = false;
  }
};

// Release modal functions
const openReleaseModal = () => {
  // Don't open modal if order cannot be released
  if (!canMarkAsReleased.value) {
    return;
  }

  releaseForm.value = {
    releaseType: '',
    notes: ''
  };
  showReleaseModal.value = true;
};

const closeReleaseModal = () => {
  showReleaseModal.value = false;
  releaseForm.value = {
    releaseType: '',
    notes: ''
  };
};

const confirmRelease = async () => {
  if (!order.value || isProcessingRelease.value) return;

  // For ready_release orders, require release type
  if (!isStuckOrder.value && !releaseForm.value.releaseType) {
    toast.error('Please select a release type');
    return;
  }

  try {
    isProcessingRelease.value = true;

    if (isStuckOrder.value) {
      // For stuck orders: Set mark_as_released flag
      const notes = releaseForm.value.notes.trim()
        ? `Order marked for automatic release (currently stuck in ${formatStatus(order.value.status)}). ${releaseForm.value.notes.trim()}`
        : `Order marked for automatic release (currently stuck in ${formatStatus(order.value.status)})`;

      const response = await api.put(`/order-status/permission-update/${order.value.id}`, {
        mark_as_released: true,
        notes: notes
      });

      if (response.data) {
        toast.success('Order marked for automatic release! Operations/QC staff will be notified.');
        closeReleaseModal();

        // Refresh the order data to show updated flag
        await loadOrderPreview();

        // Emit event to parent component to refresh the orders list
        emit('released', order.value);
      }
    } else {
      // For ready_release orders: Normal release
      let releaseNotes = `Order released via ${releaseForm.value.releaseType}`;
      if (releaseForm.value.notes.trim()) {
        releaseNotes += ` - ${releaseForm.value.notes.trim()}`;
      }

      const response = await api.put(`/order-status/permission-update/${order.value.id}`, {
        status: 'released',
        notes: releaseNotes
      });

      if (response.data) {
        toast.success('Order marked as released successfully!');
        closeReleaseModal();

        // Refresh the order data to show updated status
        await loadOrderPreview();

        // Emit event to parent component to refresh the orders list
        emit('released', order.value);
      }
    }
  } catch (error) {
    console.error('Error marking order as released:', error);
    toast.error(error.response?.data?.error || 'Failed to mark order as released');
  } finally {
    isProcessingRelease.value = false;
  }
};

// Define emits for approval and release events
const emit = defineEmits(['approved', 'rejected', 'released']);

onMounted(() => {
  loadOrderPreview();
});
</script>